import React from 'react';
import Settings from '@mui/icons-material/Settings';
import Logs from '@mui/icons-material/Assessment';
import Metrics from '@mui/icons-material/Timeline';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import Walk from '@mui/icons-material/DirectionsWalk';
import Home from '@mui/icons-material/Home';
import Dashboard from '@mui/icons-material/Dashboard';
import * as S from './AdminButtons.styled';
import { useAppSelector } from 'redux/hooks';
import { selectDiscreteAccess, selectPermission } from 'redux/global';
import { useHistory, useLocation } from 'react-router-dom';
import { clearMainBG } from 'pages/Navigation/utils';
import Logout from '@mui/icons-material/Logout';
import {
  PERM,
  canAccessCrm,
  canAccessCashierSalesTabs,
  canAccessDashboard,
  canAccessDepartmentsPage,
  canAccessInventoryPage,
  canAccessMetrics,
} from 'pages/Settings/redux';
import BranchesIcon from '@mui/icons-material/AccountTree';
import InventoryIcon from '@mui/icons-material/Inventory2';
import Booked from '@mui/icons-material/Bookmark';
import PointOfSale from '@mui/icons-material/PointOfSale';
import Replay from '@mui/icons-material/Replay';
import Apps from '@mui/icons-material/Apps';
import Business from '@mui/icons-material/Business';
import MeetingRoom from '@mui/icons-material/MeetingRoom';
import PeopleIcon from '@mui/icons-material/PeopleAlt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import FeedbackOutlinedIcon from '@mui/icons-material/FeedbackOutlined';
import AlternateEmail from '@mui/icons-material/AlternateEmail';
import { selectHotelData } from 'pages/redux';
import { resolveHotelImageSrc } from 'pages/Settings/components/hotelProfileHelpers';
import {
  HOSPITALITY_PATHS,
  erpSettingsPath,
  getErpModuleFromPath,
  isErpPagePath,
  isErpSettingsAccount,
  isHospitalityPagePath,
} from '../hospitalityModulePaths';
import { CRM_TAB_LABELS, CrmTab } from 'pages/CRM/crmTabs';

/** Navigate in-app on primary click; leave modified clicks to open the real href. */
const useSidebarNav = () => {
  const history = useHistory();
  return (href: string, e: React.MouseEvent<HTMLAnchorElement>, state?: object) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    clearMainBG();
    if (state) history.push(href, state);
    else history.push(href);
  };
};

type RailLink = {
  href: string;
  label: string;
  Icon: React.ElementType;
  isSelected: boolean;
  state?: object;
};

const AdminButtons = function AdminButtons({
  onOpenErpSidebar,
}: {
  onOpenErpSidebar: () => void;
}) {
  const permission = useAppSelector(selectPermission);
  const discreteAccess = useAppSelector(selectDiscreteAccess);
  const hotelData = useAppSelector(selectHotelData);
  const history = useHistory();
  const location = useLocation();
  const nav = useSidebarNav();
  const hotelLogoSrc = resolveHotelImageSrc(hotelData?.logo);
  const [failedHotelLogoSrc, setFailedHotelLogoSrc] = React.useState('');
  const hotelName = hotelData?.name || 'Hotel';
  const showHotelLogo = Boolean(hotelLogoSrc) && failedHotelLogoSrc !== hotelLogoSrc;
  const moduleId = getErpModuleFromPath(location.pathname) || 'hospitality';
  const settingsHref = erpSettingsPath(moduleId);
  const settingsAccountHref = erpSettingsPath(moduleId, 'account');
  const canRoomSetup = (permission || 0) >= PERM.SUB_MANAGER;

  const onClickHotelBrand = () => {
    clearMainBG();
    history.push(canAccessDashboard(permission)
      ? HOSPITALITY_PATHS.dashboard
      : HOSPITALITY_PATHS.operations);
  };

  const hospitalityLinks: RailLink[] = [
    ...(canAccessDashboard(permission) ? [{
      href: HOSPITALITY_PATHS.dashboard,
      label: 'Dashboard',
      Icon: Dashboard,
      isSelected: isHospitalityPagePath(location.pathname, 'dashboard'),
    }] : []),
    {
      href: HOSPITALITY_PATHS.operations,
      label: 'Operations',
      Icon: Home,
      isSelected: isHospitalityPagePath(location.pathname, 'operations'),
    },
    ...((permission || 0) >= PERM.FRONT_DESK ? [{
      href: HOSPITALITY_PATHS.reservations,
      label: 'Reservations',
      Icon: Booked,
      isSelected: isHospitalityPagePath(location.pathname, 'reservations'),
    }] : []),
    {
      href: HOSPITALITY_PATHS.walkIns,
      label: 'Walk-ins',
      Icon: Walk,
      isSelected: isHospitalityPagePath(location.pathname, 'walk-ins'),
    },
    ...(canRoomSetup ? [{
      href: HOSPITALITY_PATHS.roomsTab('add-rooms'),
      label: 'Room setup',
      Icon: MeetingRoom,
      isSelected: isHospitalityPagePath(location.pathname, 'rooms'),
    }] : []),
  ];

  const salesLinks: RailLink[] = [
    ...(canAccessCashierSalesTabs(permission) ? [
      {
        href: HOSPITALITY_PATHS.sales,
        label: 'Sales',
        Icon: PointOfSale,
        isSelected: isErpPagePath(location.pathname, 'sales'),
      },
      {
        href: HOSPITALITY_PATHS.refunds,
        label: 'Refunds',
        Icon: Replay,
        isSelected: isErpPagePath(location.pathname, 'refunds'),
      },
    ] : []),
    ...(canAccessDepartmentsPage(permission) ? [{
      href: HOSPITALITY_PATHS.departments,
      label: 'Departments',
      Icon: BranchesIcon,
      isSelected: isErpPagePath(location.pathname, 'departments'),
    }] : []),
    ...(canAccessInventoryPage(permission, discreteAccess) ? [{
      href: HOSPITALITY_PATHS.inventory,
      label: 'Inventory',
      Icon: InventoryIcon,
      isSelected: isErpPagePath(location.pathname, 'inventory'),
    }] : []),
  ];

  const reportingLinks: RailLink[] = [
    {
      href: HOSPITALITY_PATHS.audits,
      label: 'Audits',
      Icon: Logs,
      isSelected: isErpPagePath(location.pathname, 'audits'),
    },
    ...(canAccessMetrics(permission) ? [{
      href: HOSPITALITY_PATHS.metrics,
      label: 'Metrics',
      Icon: Metrics,
      isSelected: isErpPagePath(location.pathname, 'metrics'),
    }] : []),
    {
      href: HOSPITALITY_PATHS.reporting,
      label: 'Reporting',
      Icon: DescriptionOutlined,
      isSelected: isErpPagePath(location.pathname, 'reporting'),
    },
  ];

  const crmLinks: RailLink[] = canAccessCrm(permission)
    ? (['customers', 'feedback', 'discounts', 'campaigns', 'recipients'] as CrmTab[]).map((tab) => ({
      href: HOSPITALITY_PATHS.crmTab(tab),
      label: CRM_TAB_LABELS[tab],
      Icon: {
        customers: PeopleIcon,
        feedback: FeedbackOutlinedIcon,
        discounts: LocalOfferIcon,
        campaigns: CampaignOutlinedIcon,
        recipients: AlternateEmail,
      }[tab],
      isSelected: isErpPagePath(location.pathname, tab),
    }))
    : [];

  const moduleLinks = {
    hospitality: hospitalityLinks,
    sales_distribution: salesLinks,
    reporting_analytics: reportingLinks,
    crm: crmLinks,
  }[moduleId];

  return (
    <S.Container id="flex_bar">
      {/* Hotel mark — icon when collapsed, name on the same line when expanded. */}
      <S.AppLogo
        type="button"
        aria-label={`${hotelName} home`}
        onClick={onClickHotelBrand}
      >
        {showHotelLogo ? (
          <img
            src={hotelLogoSrc}
            alt=""
            referrerPolicy="no-referrer"
            onError={() => setFailedHotelLogoSrc(hotelLogoSrc)}
          />
        ) : (
          <Business />
        )}
        <S.HotelRailName>{hotelName}</S.HotelRailName>
      </S.AppLogo>
      <S.ErpOpenRow>
        <button type="button" aria-label="Open ERP sidebar" onClick={onOpenErpSidebar}>
          <S.Icon isSelected={false}>
            <Apps fontSize="small" />
            <S.IconText>
              Hotel
            </S.IconText>
          </S.Icon>
        </button>
      </S.ErpOpenRow>
      <S.Divider />
      <S.Icons>
        {moduleLinks.map(({ href, label, Icon, isSelected }) => (
          <a
            key={href}
            href={href}
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={(e) => nav(href, e)}
          >
            <S.Icon isSelected={isSelected}>
              <Icon fontSize="small" />
              <S.IconText>
                {label}
              </S.IconText>
            </S.Icon>
          </a>
        ))}
      </S.Icons>
      <S.Divider />
      <S.Icons2>
        <a
          href={settingsHref}
          style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={(e) => nav(settingsHref, e)}
        >
          <S.Icon isSelected={
            isErpPagePath(location.pathname, 'settings')
            && !isErpSettingsAccount(location.pathname)
          }
          >
            <Settings fontSize="small" />
            <S.IconText>
              Settings
            </S.IconText>
          </S.Icon>
        </a>
        <a
          href={settingsAccountHref}
          style={{ textDecoration: 'none', color: 'inherit' }}
          onClick={(e) => nav(settingsAccountHref, e, { openLogout: true })}
        >
          <S.Icon isSelected={false}>
            <Logout fontSize="small" />
            <S.IconText>
              Sign out
            </S.IconText>
          </S.Icon>
        </a>
      </S.Icons2>
    </S.Container>
  );
};

export default React.memo(AdminButtons);
